import { Component, OnInit, Input, OnDestroy, ElementRef } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { delay, filter, map, mergeMap } from 'rxjs/operators';
import { CalculateDialogComponent } from '../../pump-simulation/calculate-dialog/calculate-dialog.component';
import { UserLocalStorage } from '../../login/user-local-store';
import { removeRadioBtnFocus } from '../../shared/util';
import { PackagingItem } from '../controllers/controller.model';
import { DirtyComponent } from '../dirty-component';
import { DisplayProductDataService } from '../display-product-data.service';
import { Accessory, ACCESSORY_ITEMS } from '../pump-data.model';
import { SaveButtonComponent } from '../save-button/save-button.component';
import { PackagingState } from '../store/packaging/packaging.reducer';
import { selectPackaging } from '../store/packaging/packaging.selector';
import { UpdateButtonComponent } from '../update-button/update-button.component';
import { selectAccessory } from '../store/accessory/accessory.selector';
import { AccessoryState } from '../store/accessory/accessory.reducer';
import { checkAccessoryName } from '../names.validator';
import { updateAccessory } from '../store/accessory/accessory.actions';


@Component({
  selector: 'app-accessory',
  templateUrl: './accessory.component.html',
  styleUrls: ['./accessory.component.scss'],
  standalone: false
})
export class AccessoryComponent implements OnInit, OnDestroy, DirtyComponent {

  private formValueChange: Subscription = new Subscription;
  private subscriptions: Subscription[] = [];

  userLocalStorage = new UserLocalStorage();
  displayedColumns: string[] = ['approveForUse', 'version', 'status', 'published', 'author', 'comment', 'action'];
  @Input() dataSource = new MatTableDataSource();


  showVersionTable = true;
  showAccessoryForm = false;
  accessoryForm!: UntypedFormGroup;
  accessoryName = '';
  accessoryUuid = '';
  pictureUuid = '';
  accessoryData: any[] = [];
  selectedRowIndex = -1;
  myAccessory: any = {};
  myAccessoryItems: string[] = ACCESSORY_ITEMS;

  overview: any = {};

  packagingList: PackagingItem[] = [];

  isDirty = false;
  originalAccessoryFormValue: any;
  version!: string;
  versions: [] = [];
  lastVersion!: number;
  accessoryList: Accessory[] = [];

  constructor(
    private store: Store,
    private router: Router,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private displayProductData: DisplayProductDataService
  ) { }

  ngOnInit() {

    const getPackagingList = this.store.pipe(
      select(selectPackaging),
      map((data: PackagingState) => data.data)
    ).subscribe((data: PackagingItem[]) => this.packagingList = [...data].sort((a, b) => Number(a.subType.L) - Number(b.subType.L)));

    this.subscriptions.push(getPackagingList);

    const urlSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.showVersionTable = true;
      this.accessoryName = '';
      this.showAccessoryForm = false;
      this.selectedRowIndex = -1;
      this.isDirty = false;
    });
    this.subscriptions.push(urlSubscription);

    const getDataSubscription = this.route.paramMap.pipe(
      map(params => params.get('uuid')),
      mergeMap(uuid => {
        this.accessoryUuid = uuid || '';
        return this.displayProductData.getAccessoryItem(uuid || '');
      })
    ).subscribe(accessoryData => {
      this.accessoryName = accessoryData.name;
      this.pictureUuid = accessoryData.versions[accessoryData.versions.length - 1].picture_uuid;// || accessoryData.uuid;
      this.dataSource.data = accessoryData.versions;
      this.accessoryData = accessoryData.versions;
      this.myAccessory = accessoryData.versions[this.accessoryData.length - 1];
      this.lastVersion = this.accessoryData.length;
      this.showVersionTable = false;
      this.initAccessoryForm();
    });

    const getAccessoryList = this.store.pipe(
      select(selectAccessory)
    ).subscribe((list: AccessoryState) => {
      this.accessoryList = list.accessories;
    });

    this.subscriptions.push(getDataSubscription);
    this.subscriptions.push(getAccessoryList);
  }

  initAccessoryForm() {
    const accessoryFormValue: { [key: string]: any } = {};
    this.myAccessoryItems.forEach(name => {
      accessoryFormValue[`${name}`] = [null];
    });
    accessoryFormValue.name = ['', [Validators.required, checkAccessoryName.bind(this)]];
    accessoryFormValue.packaging = [null, [Validators.required]];
    accessoryFormValue.type = [null, [Validators.required]];
    accessoryFormValue.status = [null, [Validators.required]];
    accessoryFormValue.category = [null, [Validators.required]];
    accessoryFormValue.item_no_1 = [null, [Validators.required]];
    accessoryFormValue.product_name_1 = [null, [Validators.required]];
    this.accessoryForm = this.fb.group({
      ...accessoryFormValue,

      // NEW: Add a FormArray for extra attributes
      extraAttributes: this.fb.array([]),
      secondary_accessory: false,
      overview: this.fb.group({
        title: [''],
        subtitle: [''],
        description: [''],
        images: [[]],
        content: [[]]
      })
    });

    // Set the baseline for dirty checking immediately after form creation
    this.originalAccessoryFormValue = JSON.parse(JSON.stringify(this.accessoryForm.value));
    this.isDirty = false;
  }

  onOverviewChange(updatedOverview: any): void {
    // Only mark as dirty if the overview data actually changed
    const originalOverview = this.myAccessory.overview || {};
    const overviewChanged = JSON.stringify(originalOverview) !== JSON.stringify(updatedOverview);
    
    this.myAccessory.overview = updatedOverview;

    // Update form silently without triggering valueChanges
    if (this.accessoryForm.contains('overview')) {
      this.silentlyUpdateForm(() => {
        this.accessoryForm.patchValue({ overview: updatedOverview });
      });
    }

    if (overviewChanged) {
      this.isDirty = true;
    }
  }

  get extraAttributes() {
    return this.accessoryForm.get('extraAttributes') as FormArray;
  }

  addExtraAttribute() {
    this.silentlyUpdateForm(() => {
      this.extraAttributes.push(
        this.fb.group({
          field: [''],
          value: ['']
        })
      );
    });
  }


  removeExtraAttribute(index: number) {
    this.silentlyUpdateForm(() => {
      this.extraAttributes.removeAt(index);
    });
  }

  // This handles the event emitted by app-pdf-upload
  onPdfUploadComplete(event: any) {
    this.accessoryForm.patchValue({ picture_uuid: event.accessoryUuid });
    this.updateAccessory();
  }

  private silentlyUpdateForm(updateFn: () => void) {
    this.formValueChange.unsubscribe();   // stop listening
    updateFn();                           // perform the update
    this.formValueChange = this.accessoryForm.valueChanges.subscribe(value => {
      this.isDirty =
        JSON.stringify(this.originalAccessoryFormValue) !== JSON.stringify(value);
    });
  }

  onDrawingSelect(event: any) {
    console.log('Drawing_url‚ selection changed:', event);
    const drawingValue = event.value;
    const drawingName = this.extractNameFromUrl(drawingValue);
    this.accessoryForm.get('drawing_url')!.setValue(drawingValue);
    this.accessoryForm.get('drawing')!.setValue(drawingName);
    this.accessoryForm.patchValue({
      drawing: drawingName,
      drawing_url: drawingValue
    }, { emitEvent: false });
  }


  onPictureSelect($event: any) {
    console.log('Picture_url selection changed:', $event);
    const pictureValue = $event.value;
    const pictureName = this.extractNameFromUrl(pictureValue);
    this.accessoryForm.get('picture_url')!.setValue(pictureValue);
    this.accessoryForm.get('picture')!.setValue(pictureName);
    this.accessoryForm.patchValue({
      picture: pictureName,
      picture_url: pictureValue
    }, { emitEvent: false });
  }

  private extractNameFromUrl(value: string): string {
    if (!value) return '';
    // If it's a URL, extract the name
    if (value.includes('http') || value.includes('s3')) {
      const nameMatch = value.match(/\/([^\/]+)\.[^\/]+$/);
      return nameMatch ? nameMatch[1] : value;
    }
    // Otherwise it's already a name
    return value;
  }


  versionData() {
    return this.accessoryData;
  }

  showSelectedVersionData(version: string) {
    if (this.formValueChange) {
      this.formValueChange.unsubscribe();
    }

    if (!this.isDirty) {
      // the form value not changed
      this.accessoryFormValueChange(version);
    } else {
      const dialogRef = this.dialog.open(UpdateButtonComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.isDirty = false;
          this.accessoryFormValueChange(version);
        }
      });
    }
  }

  onAccessoryChange(event: any) {
    // update the local model
    this.myAccessory = event;

    // rebuild form with new keys
    const newForm: { [key: string]: any } = {};
    Object.keys(event).forEach(key => {
      newForm[key] = [event[key], Validators.required];
    });

    newForm['extraAttributes'] = this.fb.array(
      (event.extraAttributes || []).map((qc: any) => this.fb.group({
        field: [qc.field],
        value: [qc.value]
      }))
    );

    this.accessoryForm = this.fb.group(newForm);

    // update dirty comparison baseline
    this.originalAccessoryFormValue = this.accessoryForm.value;

    this.isDirty = true;
  }

  accessoryFormValueChange(version: string) {
    this.showAccessoryForm = true;
    this.selectedRowIndex = Number(version);
    for (const item of this.accessoryData) {

      if (item.version_number === Number(version)) {
        if (item.extraAttributes) {
          const arr = this.extraAttributes;
          arr.clear();

          item.extraAttributes.forEach((qc: any) => {
            arr.push(
              this.fb.group({
                field: [qc.field],
                value: [qc.value]
              })
            );
          });
        }

        const accessoryFormValue: { [key: string]: any } = {};
        Object.keys(this.myAccessory).forEach(name => accessoryFormValue[`${name}`] = item[`${name}`]);
        this.accessoryForm.patchValue(accessoryFormValue);
        this.accessoryForm.controls['secondary_accessory'].patchValue(this.accessoryForm.controls['secondary_to'].value && this.accessoryForm.controls['secondary_to'].value.length > 0);
        this.originalAccessoryFormValue = this.accessoryForm.value;

        const packageItem = this.packagingList.find(item => item.uuid === this.accessoryForm.value.packaging.uuid);
        if (packageItem) {
          this.originalAccessoryFormValue.packaging = packageItem.name;
        }
      }
    }

    this.formValueChange = this.accessoryForm.valueChanges.subscribe((value) => {
      const packagingControl = this.accessoryForm.get('packaging');
      if (packagingControl && packagingControl.value?.uuid) {
        const result = this.packagingList.filter(item => item.uuid === packagingControl.value.uuid);
        if (result.length > 0) {
          value.packaging = result[0].name;
        } else {
          value.packaging = '';
        }
      } else if (packagingControl) {
        const result = this.packagingList.filter(item => item.name && item.name.includes(packagingControl.value));
        if (result.length !== 0) {
          value.packaging = result[0].name;
        }
      }

      this.isDirty = JSON.stringify(this.originalAccessoryFormValue) !== JSON.stringify(value);
    });
  }

  onSave() {
    const dialogRef = this.dialog.open(SaveButtonComponent, {
      width: '600px',
      disableClose: true,
      minHeight: '20vh',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateAccessory();
      }
    });
  }

  updateAccessory() {
    const user = this.userLocalStorage.loadUser();
    const data = this.accessoryForm.value;
    if (!user) {
      return;
    }
    data.author = user.user.username;
    data.uuid = this.accessoryUuid;
    data.file = this.accessoryName;
    data.preview = true;

    if (this.accessoryForm.value.packaging.subType) {
      const packagingValue: any = {};
      packagingValue.name = data.packaging.uuid;
      packagingValue.w = data.packaging.subType.W;
      packagingValue.h = data.packaging.subType.H;
      packagingValue.l = data.packaging.subType.L;
      packagingValue.pack_weight = data.packaging.weight;
      data.packaging = packagingValue;
    } else {
      const result = this.packagingList.filter(item => item.name && item.name.includes(data.packaging));
      const packaging: any = {};
      packaging.name = result[0].uuid;
      packaging.w = `${result[0].subType.W}`;
      packaging.h = `${result[0].subType.H}`;
      packaging.l = `${result[0].subType.L}`;
      packaging.pack_weight = `${result[0].weight}`;
      data.packaging = packaging;
    }
    this.displayProductData.updateAccessory(data).subscribe(item => {
      const url = `${data.uuid}/${this.lastVersion}/preview`;
      this.displayProductData.setAccessoryState(url, data).subscribe(item => {

        this.snackBar
          .open(item, '', { duration: 1500, panelClass: 'hintMsg' })
          .afterDismissed()
          .pipe(delay(0))
          .subscribe(() => {
            this.ngOnInit();
            this.showVersionTable = true;
            this.accessoryName = '';
            this.showAccessoryForm = false;
            this.selectedRowIndex = -1;
            this.isDirty = false;
          });
      });
    });

    this.store.dispatch(updateAccessory(data));
  }

  canDeactivate() {
    return this.isDirty;
  }

  showSelectedVersionRadioBtn(version: string) {
    const versionNumber = Number(version) + 1;
    if (this.showAccessoryForm && (versionNumber === this.selectedRowIndex)) {
      const dialogRef = this.dialog.open(CalculateDialogComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
        data: {
          message: 'Are you sure you want to publish this version?',
          type: 'confirm'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const data = this.accessoryData[Number(version)];
          const url = `${this.accessoryUuid}/${versionNumber}/live`;
          this.displayProductData.setAccessoryState(url, data).subscribe(item => {
            this.snackBar
              .open(item, '', { duration: 1500, panelClass: 'hintMsg' })
              .afterDismissed()
              .pipe(delay(0))
              .subscribe(() => {
                this.ngOnInit();
                this.showVersionTable = true;
                this.accessoryName = '';
                this.showAccessoryForm = false;
                this.selectedRowIndex = -1;
                this.isDirty = false;
              });
          });
        } else {
          removeRadioBtnFocus(this.elementRef, Number(version));
        }
      });
    }
  }

  compareFn(obj1: any, obj2: any): boolean {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}

